// Step4BillAndLoa.jsx
//
// Wizard step 4: carrier bill upload + the authorized representative who will
// receive Twilio's LOA email after submit.
//
// Phase F (2026-05-17) reshape: the DocuSign embedded-signing flow was
// removed because Twilio's Porting API auto-generates the LOA on their
// side and emails it to the authorized representative for digital
// signature. SurFox no longer collects a signed LOA, just the data Twilio
// needs to mint and send theirs:
//   - signer_name  -> losing_carrier_information.authorized_representative
//   - signer_email -> losing_carrier_information.authorized_representative_email
// signer_title was DocuSign-template-only and is no longer collected.
//
// The file kept its `Step4BillAndLoa` filename so the wizard import in
// PortInWizard.jsx didn't churn. The "LoA" half of the name now refers to
// the Twilio-side LOA flow, not the SurFox-side DocuSign flow.

import React, { useRef, useState } from 'react';
import {
  Upload,
  Mail,
  User2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileText,
  RefreshCw,
  FileSignature,
} from 'lucide-react';
import { uploadBill } from '../../../../../../lib/portInClient';

export default function Step4BillAndLoa({ draftState }) {
  const { draft, patchTopLevel, reload } = draftState;
  const [billUploading, setBillUploading] = useState(false);
  const [billError, setBillError] = useState(null);
  const [billDragOver, setBillDragOver] = useState(false);
  const billInputRef = useRef(null);

  if (!draft) return null;

  const billUploaded = !!draft.bill_twilio_document_sid;
  const ACCEPTED_MIME = 'application/pdf,image/png,image/jpeg';
  const MAX_BILL_BYTES = 10 * 1024 * 1024;

  const onBillFile = async (file) => {
    setBillError(null);
    if (!file) return;

    if (file.size > MAX_BILL_BYTES) {
      setBillError('Bill must be 10 MB or smaller.');
      return;
    }
    const isAllowed = /^application\/pdf$|^image\/(png|jpeg|jpg)$/i.test(file.type);
    if (!isAllowed) {
      setBillError('Bill must be a PDF, PNG, or JPEG file.');
      return;
    }

    setBillUploading(true);
    try {
      const { data, error } = await uploadBill(draft.id, file);
      if (error) {
        setBillError(await extractServerError(error, 'Could not upload the bill.'));
        return;
      }
      if (data?.success) {
        await reload?.();
      }
    } catch (e) {
      setBillError(e?.message || 'Could not upload the bill.');
    } finally {
      setBillUploading(false);
      if (billInputRef.current) billInputRef.current.value = '';
    }
  };

  const onBillDrop = (e) => {
    e.preventDefault();
    setBillDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onBillFile(file);
  };

  const onBillDragOver = (e) => {
    e.preventDefault();
    if (!billDragOver) setBillDragOver(true);
  };

  const onBillDragLeave = (e) => {
    e.preventDefault();
    setBillDragOver(false);
  };

  const onReplaceBill = () => {
    if (billInputRef.current) billInputRef.current.click();
  };

  const signer = {
    name: draft.signer_name || '',
    email: draft.signer_email || '',
  };
  const signerNameValid = signer.name.trim().length > 0;
  const signerEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signer.email.trim());

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-900">
        <p className="font-medium">Final paperwork</p>
        <p className="text-orange-800/80 mt-0.5 text-xs">
          Upload a recent carrier bill and tell us who can authorize the port.
          After you submit, Twilio emails that person a Letter of Authorization
          to digitally sign. Your numbers stay live the entire time.
        </p>
      </div>

      {/* Bill panel */}
      <div
        className={`rounded-xl border p-5 ${
          billUploaded
            ? 'border-emerald-300 bg-emerald-50/60'
            : 'border-slate-200 bg-white'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              billUploaded
                ? 'bg-emerald-100 text-emerald-600'
                : 'bg-orange-100 text-orange-500'
            }`}
          >
            {billUploaded ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900">
              {billUploaded ? 'Bill uploaded' : 'Upload your recent bill'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {billUploaded
                ? 'We received your bill and forwarded a copy to Twilio. It travels with your port request.'
                : 'A recent PDF, PNG, or JPEG of your carrier bill. Less than 90 days old. Maximum 10 MB.'}
            </p>
          </div>
        </div>

        <input
          ref={billInputRef}
          type="file"
          accept={ACCEPTED_MIME}
          className="hidden"
          onChange={(e) => onBillFile(e.target.files?.[0])}
        />

        {!billUploaded && (
          <div
            onDrop={onBillDrop}
            onDragOver={onBillDragOver}
            onDragLeave={onBillDragLeave}
            className={`mt-5 p-6 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
              billDragOver
                ? 'border-orange-400 bg-orange-50'
                : 'border-slate-200 hover:border-orange-300 hover:bg-slate-50'
            }`}
            onClick={() => billInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                billInputRef.current?.click();
              }
            }}
          >
            {billUploading ? (
              <>
                <Loader2 className="w-6 h-6 mb-1.5 text-orange-500 animate-spin" />
                <p className="text-xs text-slate-500">Uploading and forwarding to Twilio</p>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 mb-1.5 text-slate-400" />
                <p className="text-xs text-slate-600 font-medium">
                  Click to upload or drag a file here
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">PDF, PNG, or JPEG. Up to 10 MB.</p>
              </>
            )}
          </div>
        )}

        {billUploaded && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 p-3 bg-white border border-emerald-200 rounded-lg text-xs text-emerald-700">
              <FileText className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-1 truncate font-mono">
                {draft.bill_storage_path?.split('/').pop() || 'carrier bill'}
              </span>
            </div>
            <button
              type="button"
              onClick={onReplaceBill}
              disabled={billUploading}
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-orange-600 disabled:opacity-40"
            >
              {billUploading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Replace
            </button>
          </div>
        )}

        {billError && (
          <div className="mt-3 flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{billError}</span>
          </div>
        )}
      </div>

      {/* Authorized representative panel */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-500 flex items-center justify-center flex-shrink-0">
            <FileSignature className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900">Authorized representative</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              The person who can authorize the port. Twilio emails them a
              Letter of Authorization to digitally sign right after you submit.
              Sign promptly so the port request does not expire.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <SignerField
            label="Full name"
            icon={User2}
            value={signer.name}
            onChange={(v) => patchTopLevel({ signer_name: v })}
            placeholder="Jane Doe"
            error={signer.name.length > 0 && !signerNameValid ? 'Name is required.' : null}
          />
          <SignerField
            label="Email"
            icon={Mail}
            type="email"
            value={signer.email}
            onChange={(v) => patchTopLevel({ signer_email: v })}
            placeholder="jane@acme.com"
            error={signer.email.length > 0 && !signerEmailValid ? 'A valid email is required.' : null}
          />
        </div>

        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 text-[11px] text-slate-500 leading-relaxed">
          We send these details to Twilio. Twilio sends the actual LOA email
          and captures the digital signature. Nothing for you to sign here.
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-xs text-slate-500">
        <p>
          Once the bill is uploaded and both fields above are filled, continue
          to the Review step to confirm everything and submit to the losing
          carrier. Your numbers stay live the entire time.
        </p>
      </div>
    </div>
  );
}

function SignerField({ label, icon: Icon, value, onChange, placeholder, error, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      <div
        className={`flex items-center gap-2 px-3 py-2 bg-white border rounded-lg ${
          error ? 'border-red-300' : 'border-slate-200'
        } focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100`}
      >
        <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 text-sm text-slate-900 placeholder-slate-300 bg-transparent outline-none"
        />
      </div>
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
    </div>
  );
}

/**
 * Extract a server-rendered error message from a supabase-js
 * FunctionsHttpError. The `context` field can be either a raw Response
 * object (multipart uploads, recent supabase-js versions) or a stale
 * pre-parsed shape (older versions or non-multipart paths). Falls back to
 * the generic supabase-js wrapper message ("Edge Function returned a
 * non-2xx status code") only when both extraction paths fail.
 */
async function extractServerError(error, fallback) {
  const ctx = error?.context;
  if (ctx) {
    if (typeof ctx.text === 'function') {
      try {
        const bodyText = await ctx.clone().text();
        if (bodyText) {
          try {
            const parsed = JSON.parse(bodyText);
            return parsed?.error || parsed?.message || bodyText;
          } catch {
            return bodyText;
          }
        }
      } catch {
        // fall through to next strategy
      }
    }
    if (typeof ctx.body === 'string') {
      try {
        const parsed = JSON.parse(ctx.body);
        return parsed?.error || parsed?.message || ctx.body;
      } catch {
        return ctx.body;
      }
    }
    if (ctx.body && typeof ctx.body === 'object') {
      return ctx.body.error || ctx.body.message || JSON.stringify(ctx.body);
    }
  }
  return error?.message || fallback;
}
